import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { VariableSizeList as List } from "react-window";
import classNames from "classnames";
import { useActivity } from "../hooks";
import ActivityDayList from "./ActivityDayList";
import { getActivitiesByDate } from "./helpers";

import classes from "./ActivityList.module.scss";
import useResizeObserver from "@react-hook/resize-observer";
import LsLoading from "Components/LsLoading";
import { toDate } from "Utils/date";
import ActivityNullState from "./ActivityNullState";

interface ActivityListProps {
   className?: string;
}

const ActivityList: React.FC<ActivityListProps> = ({ className }) => {
   const { activities, loading } = useActivity();
   const heightsRef = useRef<number[]>([]);
   const listContainerRef = useRef<HTMLDivElement>(null);
   const listRef = useRef<List>(null);
   const [listHeight, setListHeight] = useState(0);

   useLayoutEffect(() => {
      heightsRef.current = [];
      listRef.current?.scrollTo(0);
   }, [activities]);

   const activitiesByDate = useMemo(
      () => getActivitiesByDate(activities),
      [activities]
   );

   const dateList = useMemo(
      () => Object.keys(activitiesByDate).sort((a, b) => b.localeCompare(a)),
      [activitiesByDate]
   );

   const getItemSize = (index) => {
      const date = dateList[index];
      const activities = activitiesByDate[date];
      const defaultHeight = 48 + activities.length * 107.2;
      const height = heightsRef.current[index];
      return height ?? defaultHeight;
   };

   const setHeight = useCallback((index: number, height: number) => {
      heightsRef.current[index] = Math.ceil(height);
      const previousIndex = Math.max(0, index - 1);
      listRef.current?.resetAfterIndex(previousIndex);
   }, []);

   const initialScrollOffset = useMemo(() => {
      if (dateList.length > 0) {
         const today = new Date();
         const nearestDateIndex = dateList.findLastIndex(d => toDate(d) >= toDate(today.toDateString()));
         const startDateIndex = nearestDateIndex == -1 ? 0 : nearestDateIndex;
         return [...Array(startDateIndex).keys()].reduce((acc, _, i) => acc + getItemSize(i), 0);
      }
      return 0;
   }, [dateList]);

   useResizeObserver(listContainerRef.current, entry => {
      setListHeight(listContainerRef.current?.clientHeight ?? 0);
   });

   return (
      <div className={className} ref={listContainerRef}>
         {
            loading
               ? <LsLoading isLoading />
               : dateList.length > 0
                  ? <List
                     height={listHeight}
                     className={classNames(classes.list)}
                     itemCount={dateList.length}
                     itemData={dateList}
                     width="100%"
                     itemSize={getItemSize}
                     ref={listRef}
                     initialScrollOffset={initialScrollOffset}
                  >
                     {({ index, style, data }) => {
                        const date = data[index];
                        return (
                           <div style={style}>
                              <ActivityDayList
                                 index={index}
                                 date={date}
                                 activities={activitiesByDate[date]}
                                 className={classes.dropdownUserMenuListItem}
                                 setHeight={setHeight}
                              />
                           </div>
                        );
                     }}
                  </List>
                  : <ActivityNullState />
         }
      </div>
   );
};

export default ActivityList;
